import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash-es';
import QuickSearchResult from './QuickSearchResult';
import { uniqueId } from 'lodash';
import { translate } from '../../Services/translation';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import {
  query,
  setSearchQuery,
  handleKeyDown,
  toggleInputActive,
} from '../../Actions/QuickSearch.action';
import QuickSearchInitialTab from './QuickSearch.InitialTab';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { QuickSearchModel } from '../../Types/QuickSearchModel';
import Icon from '../Icon';
import clsx from 'clsx';

const debouncedQuery = debounce(
  (dispatch, text) => dispatch(query(text)),
  200
);

const QuickSearch = (props: QuickSearchModel) => {
  const quickSearch = useTypedSelector((state) => state.quickSearch);
  const { query, result, showResult, activeTab, inputActive } =
    quickSearch;
  const [id] = useState(uniqueId());
  const searchContainer = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const searchUrl =
    global['LitiumQuickSearchUrl'] +
    (query.length > 0 ? `?q=${query}` : '');
  const triggerButtonElement: HTMLElement | null = document.querySelector(
    '.quick-search-trigger'
  );

  const getFocusableElements = useCallback(() => {
    if (!searchContainer.current) return [];

    return Array.from(
      searchContainer.current.querySelectorAll<HTMLElement>(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
    );
  }, [searchContainer]);

  const handleTabKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement &&
        lastElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement &&
        firstElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    [getFocusableElements]
  );

  const handleEndKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'End') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const lastElement = focusableElements[focusableElements.length - 1];
      if (lastElement && document.activeElement !== lastElement) {
        event.preventDefault();
        lastElement.focus();
      }
    },
    [getFocusableElements]
  );

  const handleHomeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Home') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      if (firstElement && document.activeElement !== firstElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    [getFocusableElements]
  );

  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(toggleInputActive(false));
        triggerButtonElement?.focus();
      }
    },
    [dispatch, triggerButtonElement]
  );

  useEffect(() => {
    if (inputActive && searchInput.current) {
      searchInput.current.focus();
    }
  }, [inputActive, searchInput]);

  useEffect(() => {
    const handleKeydownEvents = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscKey(event);
      } else if (event.key === 'Tab') {
        handleTabKey(event);
      } else if (event.key === 'Home') {
        handleHomeKey(event);
      } else if (event.key === 'End') {
        handleEndKey(event);
      }
    };

    if (inputActive) {
      document.addEventListener('keydown', handleKeydownEvents);
      dispatch(
        addOverlay('quick-search__' + id, () =>
          dispatch(toggleInputActive(false))
        )
      );
    } else {
      document.removeEventListener('keydown', handleKeydownEvents);
      dispatch(removeOverlay('quick-search__' + id));
    }
    return () => {
      dispatch(removeOverlay('quick-search__' + id));
      document.removeEventListener('keydown', handleKeydownEvents);
    };
  }, [
    dispatch,
    inputActive,
    id,
    handleEscKey,
    handleTabKey,
    handleHomeKey,
    handleEndKey,
  ]);

  const items = useMemo(() => result?.results, [result]);
  const totalNumResults = useMemo(
    () =>
      items?.reduce((total, curr) => curr.items.length + total, 0) ?? 0,
    [items]
  );
  const noHits = useMemo(() => totalNumResults > 0, [totalNumResults]);

  const handleOnClearClick = () => {
    dispatch(setSearchQuery(''));
    debouncedQuery(dispatch, '');
    if (searchInput.current) {
      searchInput.current.focus();
    }
  };

  return (
    <>
      {inputActive ? (
        <div className="grow" role="search" ref={searchContainer}>
          <div
            className={clsx(
              'relative flex h-[100vh] w-full flex-col items-center overflow-y-auto rounded-b bg-white pt-4 scrollbar-none lg:pb-16 lg:pt-9',
              noHits ? 'lg:h-[720px]' : 'lg:h-[383px]'
            )}
            role="search"
          >
            <div className="container flex w-full max-w-[1888px] flex-col !gap-0 lg:px-6 xl:px-8">
              <div className="w-full pb-4">
                <div className="relative py-1">
                  <input
                    className={clsx(
                      'h2 flex h-10 w-full rounded-none bg-transparent text-gray-700 search-decoration:hidden [&:focus]:outline-none'
                    )}
                    type="text"
                    placeholder={translate('general.search')}
                    autoComplete="off"
                    value={decodeURIComponent(query)}
                    onChange={(event) => {
                      const text = encodeURIComponent(event.target.value);
                      dispatch(setSearchQuery(text));
                      debouncedQuery(dispatch, text);
                    }}
                    onClick={() => {
                      dispatch(toggleInputActive(true));
                      searchInput.current?.focus();
                    }}
                    onKeyDown={(event) => {
                      dispatch(handleKeyDown(event, { searchUrl }));
                      if (event.key == 'Enter' && searchUrl) {
                        window.location.href = searchUrl;
                      }
                    }}
                    ref={searchInput}
                    autoFocus
                  />
                  <div
                    className={clsx(
                      'absolute right-0 top-1/2 m-0 flex translate-y-[-50%] items-center',
                      inputActive && 'text-gray-500'
                    )}
                  >
                    {query.length > 0 && (
                      <button
                        className="mr-2 py-2 pl-2 text-sm text-gray-500"
                        onClick={() => handleOnClearClick()}
                      >
                        {translate('quick-search.clear')}
                      </button>
                    )}
                    <a className="p-2" href={searchUrl}>
                      <Icon
                        name={'magnifier'}
                        className="h-6 w-6 text-gray-700 lg:h-8 lg:w-8"
                      />
                    </a>
                  </div>
                </div>
              </div>
              <hr className="w-full border-gray-100" />
            </div>
            {showResult && result && (
              <QuickSearchResult
                searchUrl={searchUrl}
                noHitDescription={props.noHitDescription}
                noHitHeader={props.noHitHeader}
                activeTab={activeTab}
                items={items}
                totalNumResults={totalNumResults}
              />
            )}

            {!showResult && inputActive && (
              <QuickSearchInitialTab {...props} />
            )}
          </div>
          {noHits && (
            <div className="fixed bottom-0 left-0 z-[60] flex h-16 w-full items-center justify-center border-t border-solid border-gray-100 bg-white py-2 lg:absolute">
              <div className="container w-full max-w-[1888px] lg:px-6 xl:px-8">
                <a
                  href={searchUrl}
                  className="btn btn--black btn--large w-full "
                >
                  {translate('quicksearch.showmore')}
                </a>
              </div>
            </div>
          )}
          <button
            className="sr-only focus:not-sr-only focus:absolute focus:right-6 focus:top-1/2 focus:z-[70] focus:rounded focus:bg-gray-100 focus:p-3 focus:shadow-md focus:xl:right-8"
            aria-label={translate('general.close')}
            onClick={() => {
              dispatch(toggleInputActive(false));
              triggerButtonElement?.focus();
            }}
          >
            {translate('general.close')}
          </button>
        </div>
      ) : null}
    </>
  );
};

export default QuickSearch;
